import React, { useState } from 'react';
import styled from 'styled-components/macro';
import {
  CModal,
  ModalOverlay,
  ModalContent,
  CModalHeader,
  CModalFooter,
  CModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Spinner,
  Tooltip,
  Box,
} from '@televet/kibble-ui';
import {
  SurveyStep,
  WorkflowEvent,
  useFindManyAppointmentAutomationsQuery,
  useTriggerDemoWorkflowEventMutation,
  SurveyType,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import sortBy from 'lodash-es/sortBy';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';

interface IDispatchAutomationButtonProps {
  clinicPetIds: string[] | undefined;
  smallButton?: boolean;
}

const DispatchAutomationButton = ({ clinicPetIds, smallButton }: IDispatchAutomationButtonProps): JSX.Element => {
  const [triggerResponse, setTriggerResponse] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isFeatureEnabled } = useFeatureFlag();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data, loading } = useFindManyAppointmentAutomationsQuery({
    variables: {
      where: { clinicPet: { id: { in: clinicPetIds } }, startAt: { gte: today } },
    },
    skip: !clinicPetIds || !isOpen,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const [triggerWorkflow] = useTriggerDemoWorkflowEventMutation();

  const handleDispatch = async (appointmentId: string, automationId: string, event: WorkflowEvent): Promise<void> => {
    setTriggerResponse(false);
    const response = await triggerWorkflow({
      variables: {
        data: {
          entityId: appointmentId,
          workflowId: automationId,
          event: event,
          delay: 1000,
          actionNumber: 1,
        },
      },
    });
    setTriggerResponse(response?.data?.triggerWorkflowEvent?.success || false);
  };

  return (
    <Box>
      <Tooltip label="Send Automation">
        <Button
          data-testid="send-automation-button"
          onClick={onOpen}
          variant="ghostNeutral"
          iconName="robot"
          size={smallButton ? 'sm' : 'md'}
        />
      </Tooltip>

      <CModal isOpen={isOpen} onClose={onClose} size={'5xl'} scrollBehavior={'inside'}>
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>
            <ModalTitle>
              Send Automation
              <DispatchStatus>{triggerResponse && 'Automation sent successfully'}</DispatchStatus>
            </ModalTitle>
          </CModalHeader>
          <ModalCloseButton />
          <CModalBody>
            <AvailableAutomations>
              {!loading ? (
                <Dispatcher>
                  {data?.findManyAppointment?.map((appointment) => {
                    return (
                      <Appointment key={appointment.id}>
                        <AppointmentData>
                          <AppointmentLabel>Pet:</AppointmentLabel>
                          <AppointmentField>{appointment.clinicPet?.name}</AppointmentField>
                          <AppointmentLabel>Appointment Type:</AppointmentLabel>
                          <AppointmentField>{appointment.appointmentType?.name}</AppointmentField>
                          <AppointmentLabel>Description:</AppointmentLabel>
                          <AppointmentField>{appointment.description}</AppointmentField>
                        </AppointmentData>
                        <Automations>
                          {sortBy(
                            appointment.workflows.filter(
                              (automation) =>
                                automation.event !== WorkflowEvent.ServiceReminderFormSubmitted &&
                                automation.event !== WorkflowEvent.WidgetRequestFormSubmitted &&
                                automation.event !== WorkflowEvent.CallDeflectionFormSubmitted &&
                                automation.isPublished &&
                                (!automation.surveySettingId ||
                                  (automation.surveyStep === SurveyStep.Rating &&
                                    ((isFeatureEnabled(FeatureFlagName.NpsSurvey) &&
                                      automation.surveyType === SurveyType.Nps) ||
                                      automation.surveyType === SurveyType.Csat))),
                            ),
                            (automation) => automation.name.toLowerCase(),
                          ).map((workflowEventSetting) => {
                            return (
                              <Automation key={`${appointment.id}-${workflowEventSetting.id}`}>
                                <AutomationName>{workflowEventSetting?.name}</AutomationName>
                                <Button
                                  onClick={(): void => {
                                    handleDispatch(appointment.id, workflowEventSetting.id, workflowEventSetting.event);
                                  }}
                                >
                                  Send
                                </Button>
                              </Automation>
                            );
                          })}
                        </Automations>
                      </Appointment>
                    );
                  })}
                  {!data?.findManyAppointment?.length && 'No automations found for the pets in this channel'}
                </Dispatcher>
              ) : (
                <Spinner />
              )}
            </AvailableAutomations>
          </CModalBody>

          <CModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
          </CModalFooter>
        </ModalContent>
      </CModal>
    </Box>
  );
};

const ModalTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-right: 30px;
`;

const DispatchStatus = styled.div`
  display: flex;
  color: #5cc098;
  justify-content: flex-end;
  align-items: center;
  font-size: 16px;
  text-align: end;
`;

const AvailableAutomations = styled.div`
  display: flex;
  flex-direction: column;
`;

const Dispatcher = styled.div``;

const Appointment = styled.div`
  display: flex;
  flex-direction: column;
`;

const AppointmentData = styled.div`
  display: flex;
`;

const AppointmentLabel = styled.div`
  font-weight: 600;
`;

const AppointmentField = styled.div`
  margin: 0 10px;
`;

const Automations = styled.div`
  display: flex;
  flex-direction: column;
`;

const Automation = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 10px;
`;

const AutomationName = styled.div`
  margin-right: 21px;
`;

export default DispatchAutomationButton;
